import { useEffect, useRef, type ReactNode } from "react";

/** Real CSS perspective keeps the content, highlights and beveled backing on separate planes. */
export function DepthCard({
  as: Element = "div", children, className = "", id,
}: { as?: "div" | "article"; children: ReactNode; className?: string; id?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const card = ref.current;
    if (!card || typeof window.matchMedia !== "function") return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const observer = new IntersectionObserver(([entry]) => {
      card.dataset.visible = String(entry?.isIntersecting ?? false);
    });
    observer.observe(card);
    let frame = 0;
    let bounds: DOMRect | undefined;
    let x = 0.5;
    let y = 0.5;
    const reset = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      bounds = undefined;
      delete card.dataset.engaged;
      for (const property of ["--tilt-x", "--tilt-y", "--shine-x", "--shine-y"]) card.style.removeProperty(property);
    };
    const draw = () => {
      frame = 0;
      card.style.setProperty("--tilt-x", `${(0.5 - y) * 9}deg`);
      card.style.setProperty("--tilt-y", `${(x - 0.5) * 11}deg`);
      card.style.setProperty("--shine-x", `${x * 100}%`);
      card.style.setProperty("--shine-y", `${y * 100}%`);
    };
    const move = (event: PointerEvent) => {
      if (motion.matches || !pointer.matches || event.pointerType === "touch" || card.matches(":focus-within")) return;
      bounds ??= card.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      x = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
      y = Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height));
      card.dataset.engaged = "true";
      if (!frame) frame = requestAnimationFrame(draw);
    };
    card.addEventListener("pointermove", move, { passive: true });
    card.addEventListener("pointerleave", reset);
    card.addEventListener("pointercancel", reset);
    card.addEventListener("focusin", reset);
    window.addEventListener("blur", reset);
    motion.addEventListener("change", reset);
    pointer.addEventListener("change", reset);
    return () => {
      reset();
      observer.disconnect();
      card.removeEventListener("pointermove", move);
      card.removeEventListener("pointerleave", reset);
      card.removeEventListener("pointercancel", reset);
      card.removeEventListener("focusin", reset);
      window.removeEventListener("blur", reset);
      motion.removeEventListener("change", reset);
      pointer.removeEventListener("change", reset);
    };
  }, []);
  return (
    <Element ref={ref} id={id} className={`community-depth-card ${className}`}>
      {children}
      <span aria-hidden="true" className="community-card-sheen" />
    </Element>
  );
}
